import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Plus, ChatCircleText, Trash, Pencil, Warning, CheckCircle, ClipboardText } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import ESignature from '../components/ESignature';
import { useAuth } from '../contexts/AuthContext';
import { sortByVCode } from '../utils/sortVolunteers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TARGET_ROLES = [
  { value: 'investigador_principal', es: 'Investigador Principal', en: 'Principal Investigator' },
  { value: 'investigador', es: 'Co-Investigador', en: 'Co-Investigator' },
  { value: 'reclutamiento', es: 'Reclutamiento', en: 'Recruitment' },
  { value: 'sondas', es: 'Sondas', en: 'Probes' },
];

const STATUSES = [
  { value: 'en_curso', es: 'Abierta', en: 'Open', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'respondida', es: 'Respondida', en: 'Answered', color: 'bg-sky-50 text-sky-700 border-sky-200' },
  { value: 'terminada', es: 'Cerrada', en: 'Closed', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];

const roleLabel = (value, lang) => {
  const r = TARGET_ROLES.find(x => x.value === value);
  return r ? (lang === 'es' ? r.es : r.en) : value;
};

const statusCfg = (value) => STATUSES.find(s => s.value === value) || STATUSES[0];

export default function QueriesPanel({ studyId, lang = 'es', autoOpenQueryId = null }) {
  const navigate = useNavigate();
  const openQuestionnaire = (q) => navigate(
    `/studies/${studyId}?tab=volunteers&volunteer_id=${q.volunteer_id}&open_q=${q.visit_id}:${q.questionnaire_id}:${q.entry_index || 0}&query_id=${q.query_id}`
  );
  const { user } = useAuth();
  const isAdmin = (user?.roles || [user?.role]).includes('admin');
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmClose, setConfirmClose] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const lb = lang === 'es' ? {
    title: 'Queries del Estudio',
    new: 'Nueva Query',
    no_items: 'No hay queries registradas',
    hint: 'Crea una nueva query para comunicar incidencias al staff del estudio',
    code: 'Codigo', target: 'Para', volunteer: 'Voluntario', visit: 'Visita',
    observations: 'Observaciones', status: 'Estado', created: 'Creada',
    created_by: 'Por', actions: 'Acciones',
    dlg_new: 'Nueva Query', dlg_edit: 'Editar Query',
    select_role: 'Seleccionar rol...', select_vol: 'Seleccionar voluntario...',
    select_visit: 'Seleccionar visita...',
    obs_placeholder: 'Describa la incidencia, duda o observacion sobre la visita...',
    save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', edit: 'Editar',
    required_all: 'Todos los campos son obligatorios',
    created_ok: 'Query creada correctamente',
    updated_ok: 'Query actualizada',
    closed_ok: 'Query terminada',
    deleted_ok: 'Query eliminada',
    close_title: 'Terminar Query',
    close_warn: 'Esta accion NO se puede deshacer. Una vez terminada, la query quedara bloqueada y no podra modificarse ni reabrirse. Se requiere firma electronica.',
    confirm_close: 'Terminar Query',
    del_title: 'Eliminar Query',
    del_warn: 'Esta accion eliminara permanentemente la query. ?Continuar?',
    confirm_del: 'Eliminar',
    no_included: 'No hay voluntarios incluidos en este estudio',
    no_visits: 'No hay visitas definidas en el protocolo',
    closed_info: 'Query terminada - no editable',
    tip_edit: 'Editar', tip_close: 'Finalizar', tip_delete: 'Eliminar',
  } : {
    title: 'Study Queries',
    new: 'New Query',
    no_items: 'No queries registered',
    hint: 'Create a new query to communicate issues to the study staff',
    code: 'Code', target: 'To', volunteer: 'Volunteer', visit: 'Visit',
    observations: 'Observations', status: 'Status', created: 'Created',
    created_by: 'By', actions: 'Actions',
    dlg_new: 'New Query', dlg_edit: 'Edit Query',
    select_role: 'Select role...', select_vol: 'Select volunteer...',
    select_visit: 'Select visit...',
    obs_placeholder: 'Describe the issue, question or observation about the visit...',
    save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit',
    required_all: 'All fields are required',
    created_ok: 'Query created',
    updated_ok: 'Query updated',
    closed_ok: 'Query closed',
    deleted_ok: 'Query deleted',
    close_title: 'Close Query',
    close_warn: 'This action CANNOT be undone. Once closed, the query will be locked and cannot be modified or reopened. Electronic signature required.',
    confirm_close: 'Close Query',
    del_title: 'Delete Query',
    del_warn: 'This will permanently delete the query. Continue?',
    confirm_del: 'Delete',
    no_included: 'No included volunteers in this study',
    no_visits: 'No visits defined in the protocol',
    closed_info: 'Query closed - read only',
    tip_edit: 'Edit', tip_close: 'Close', tip_delete: 'Delete',
  };

  const load = useCallback(async () => {
    if (!studyId) return;
    setLoading(true);
    try {
      const [q, v, vs] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/queries`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }),
      ]);
      setQueries(q.data);
      setVisits(v.data);
      // URS-F323 + URS-F494m: incluir TODOS los voluntarios asignados al
      // estudio (cualquier estado: pendiente, apto, no_apto, incluido,
      // excluido, abandono) para poder levantar queries sobre cualquiera.
      // Orden principal: V-code natural (V1, V2, ..., V10). Los que aun
      // no tienen V-code asignado (pendientes/aptos/no_aptos) van al
      // final ordenados por nombre.
      const allVols = (vs.data || []);
      const sorted = sortByVCode(allVols);
      // Segundo orden alfabetico para los que sortByVCode dejo al final
      // (sin V-code). Los mantenemos juntos al final por nombre asc.
      const withCode = sorted.filter(v => v.study_volunteer_code);
      const withoutCode = sorted
        .filter(v => !v.study_volunteer_code)
        .sort((a, b) => (a.volunteer_name || '').localeCompare(b.volunteer_name || '', 'es'));
      setVolunteers([...withCode, ...withoutCode]);
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
    setLoading(false);
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  // Auto-open edit dialog when navigating from Study Dashboard with ?query_id=...
  useEffect(() => {
    if (!autoOpenQueryId || loading || queries.length === 0) return;
    const q = queries.find(x => x.query_id === autoOpenQueryId);
    if (q && q.questionnaire_id) { openQuestionnaire(q); return; }
    if (q) {
      setEditing({ ...q });
      setShowForm(true);
    }
  }, [autoOpenQueryId, loading, queries]);

  const openNew = () => {
    setEditing({
      target_role: '',
      volunteer_id: '',
      study_volunteer_code: '',
      visit_id: '',
      visit_code: '',
      observations: '',
    });
    setShowForm(true);
  };

  const openEdit = (q) => {
    setEditing({ ...q });
    setShowForm(true);
  };

  const saveQuery = async () => {
    if (!editing.target_role || !editing.volunteer_id || !editing.visit_id || !editing.observations.trim()) {
      toast.error(lb.required_all);
      return;
    }
    try {
      if (editing.query_id) {
        await axios.put(`${API_URL}/api/studies/${studyId}/queries/${editing.query_id}`, editing, { withCredentials: true });
        toast.success(lb.updated_ok);
      } else {
        await axios.post(`${API_URL}/api/studies/${studyId}/queries`, editing, { withCredentials: true });
        toast.success(lb.created_ok);
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  const handleCloseQuery = async (pin, reason) => {
    await axios.post(
      `${API_URL}/api/studies/${studyId}/queries/${confirmClose.query_id}/close`,
      { pin, reason },
      { withCredentials: true }
    );
    toast.success(lb.closed_ok);
    setShowSignature(false);
    setConfirmClose(null);
    load();
  };

  const deleteQuery = async () => {
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/queries/${confirmDelete.query_id}`, { withCredentials: true });
      toast.success(lb.deleted_ok);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(errText(e, 'Error'));
    }
  };

  return (
    <div className="space-y-4" data-testid="queries-panel">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-heading font-medium text-stone-900 flex items-center gap-2">
          <ChatCircleText size={20} className="text-[#1f3e80]" />
          {lb.title}
          {queries.length > 0 && (
            <Badge variant="outline" className="text-xs font-mono bg-stone-50">{queries.length}</Badge>
          )}
        </h2>
        <Button data-testid="new-query-btn" onClick={openNew} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={16} />{lb.new}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-stone-400 text-sm">...</div>
      ) : queries.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <ChatCircleText size={40} className="text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500 font-medium">{lb.no_items}</p>
          <p className="text-xs text-stone-400 mt-1">{lb.hint}</p>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-left text-xs font-medium text-stone-600">
                <th className="px-4 py-2.5">{lb.code}</th>
                <th className="px-4 py-2.5">{lb.target}</th>
                <th className="px-4 py-2.5">{lb.volunteer}</th>
                <th className="px-4 py-2.5">{lb.visit}</th>
                <th className="px-4 py-2.5">{lb.status}</th>
                <th className="px-4 py-2.5">{lb.created}</th>
                <th className="px-4 py-2.5 text-right">{lb.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {queries.map(q => {
                const cfg = statusCfg(q.status);
                const isClosed = q.status === 'terminada';
                return (
                  <tr key={q.query_id} data-testid={`query-row-${q.code}`} className="hover:bg-stone-50/60 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs text-stone-700">{q.code}</td>
                    <td className="px-4 py-2.5">{roleLabel(q.target_role, lang)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{q.study_volunteer_code}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">
                      {q.visit_code}
                      {q.questionnaire_id && (
                        <button type="button" data-testid={`query-open-questionnaire-${q.code}`} onClick={() => openQuestionnaire(q)}
                          className="mt-0.5 flex items-center gap-1 font-sans text-[11px] text-[#1f3e80] hover:underline text-left">
                          <ClipboardText size={11} />{q.questionnaire_name || (lang === 'es' ? 'Cuestionario' : 'Questionnaire')}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>
                        {lang === 'es' ? cfg.es : cfg.en}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-stone-500">
                      <div>{new Date(q.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-GB')}</div>
                      <div className="text-[10px] text-stone-400">{q.created_by_name || q.created_by}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <TooltipProvider delayDuration={200}>
                        <div className="flex items-center justify-end gap-1">
                          {!isClosed && !q.questionnaire_id && (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm" variant="outline" data-testid={`edit-query-${q.code}`}
                                    onClick={() => openEdit(q)}
                                    className="h-7 px-2 text-xs rounded-sm"
                                  >
                                    <Pencil size={12} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{lb.tip_edit}</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm" variant="outline" data-testid={`close-query-${q.code}`}
                                    onClick={() => setConfirmClose(q)}
                                    className="h-7 px-2 text-xs rounded-sm text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                  >
                                    <CheckCircle size={12} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{lb.tip_close}</TooltipContent>
                              </Tooltip>
                            </>
                          )}
                          {isAdmin && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm" variant="outline" data-testid={`delete-query-${q.code}`}
                                  onClick={() => setConfirmDelete(q)}
                                  className="h-7 px-2 text-xs rounded-sm text-rose-600 border-rose-200 hover:bg-rose-50"
                                >
                                  <Trash size={12} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">{lb.tip_delete}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TooltipProvider>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={o => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent data-testid="query-form-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.query_id ? lb.dlg_edit : lb.dlg_new}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {editing.query_id && editing.status === 'terminada' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm text-xs text-emerald-800">
                  <CheckCircle size={14} />{lb.closed_info}
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{lb.target} <span className="text-red-500">*</span></Label>
                <Select
                  value={editing.target_role}
                  onValueChange={v => setEditing({ ...editing, target_role: v })}
                >
                  <SelectTrigger data-testid="query-target-select" className="bg-white border-stone-200">
                    <SelectValue placeholder={lb.select_role} />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{lang === 'es' ? r.es : r.en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">{lb.volunteer} <span className="text-red-500">*</span></Label>
                  <Select
                    value={editing.volunteer_id}
                    onValueChange={v => {
                      const vol = volunteers.find(x => x.volunteer_id === v);
                      setEditing({ ...editing, volunteer_id: v, study_volunteer_code: vol?.study_volunteer_code || '' });
                    }}
                  >
                    <SelectTrigger data-testid="query-volunteer-select" className="bg-white border-stone-200">
                      <SelectValue placeholder={lb.select_vol} />
                    </SelectTrigger>
                    <SelectContent>
                      {volunteers.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-stone-400">{lb.no_included}</div>
                      ) : volunteers.map(v => {
                        const statusBadge = {
                          pendiente: { es: 'Pendiente', en: 'Pending', cls: 'bg-stone-50 text-stone-600 border-stone-200' },
                          apto: { es: 'Apto', en: 'Eligible', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                          no_apto: { es: 'No apto', en: 'Not eligible', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
                          abandono: { es: 'Abandono', en: 'Withdrawal', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                          excluido: { es: 'Excluido', en: 'Excluded', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
                        }[v.status];
                        return (
                          <SelectItem key={v.volunteer_id} value={v.volunteer_id}>
                            <span className="inline-flex items-center gap-2">
                              <span className="font-mono w-8 inline-block text-stone-700">
                                {v.study_volunteer_code || '—'}
                              </span>
                              <span className="text-stone-700">{v.volunteer_name || v.volunteer_id}</span>
                              {statusBadge && v.status !== 'incluido' && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${statusBadge.cls}`}>
                                  {lang === 'es' ? statusBadge.es : statusBadge.en}
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">{lb.visit} <span className="text-red-500">*</span></Label>
                  <Select
                    value={editing.visit_id}
                    onValueChange={v => {
                      const vis = visits.find(x => x.visit_id === v);
                      setEditing({ ...editing, visit_id: v, visit_code: vis?.code || '' });
                    }}
                  >
                    <SelectTrigger data-testid="query-visit-select" className="bg-white border-stone-200">
                      <SelectValue placeholder={lb.select_visit} />
                    </SelectTrigger>
                    <SelectContent>
                      {visits.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-stone-400">{lb.no_visits}</div>
                      ) : visits.map(v => (
                        <SelectItem key={v.visit_id} value={v.visit_id}>
                          <span className="font-mono">{v.code}</span>{v.name ? ` — ${v.name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{lb.observations} <span className="text-red-500">*</span></Label>
                <Textarea
                  data-testid="query-observations-input"
                  value={editing.observations}
                  onChange={e => setEditing({ ...editing, observations: e.target.value })}
                  placeholder={lb.obs_placeholder}
                  className="bg-white border-stone-200 min-h-[120px]"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }} className="rounded-sm">
              {lb.cancel}
            </Button>
            <Button
              data-testid="save-query-btn"
              onClick={saveQuery}
              className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm"
            >
              {lb.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm close dialog (irreversible) */}
      <Dialog open={!!confirmClose} onOpenChange={o => { if (!o) setConfirmClose(null); }}>
        <DialogContent data-testid="confirm-close-query-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <Warning size={20} weight="fill" className="text-amber-600" />
              {lb.close_title}
            </DialogTitle>
            <DialogDescription className="pt-2 text-stone-700">
              {lb.close_warn}
            </DialogDescription>
          </DialogHeader>
          {confirmClose && (
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-stone-500">{lb.code}:</span><span className="font-mono">{confirmClose.code}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">{lb.target}:</span><span>{roleLabel(confirmClose.target_role, lang)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">{lb.volunteer}:</span><span className="font-mono">{confirmClose.study_volunteer_code}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">{lb.visit}:</span><span className="font-mono">{confirmClose.visit_code}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClose(null)} className="rounded-sm">
              {lb.cancel}
            </Button>
            <Button
              data-testid="confirm-close-query-btn"
              onClick={() => setShowSignature(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
            >
              {lb.confirm_close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ESignature for closing */}
      <ESignature
        open={showSignature}
        onClose={() => setShowSignature(false)}
        onSubmitPin={handleCloseQuery}
        action={lang === 'es' ? 'Terminar Query' : 'Close Query'}
        resource={confirmClose?.code || ''}
        lang={lang}
      />

      {/* Confirm delete dialog (admin only) */}
      <Dialog open={!!confirmDelete} onOpenChange={o => { if (!o) setConfirmDelete(null); }}>
        <DialogContent data-testid="confirm-delete-query-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-rose-800">{lb.del_title}</DialogTitle>
            <DialogDescription className="pt-2 text-stone-700">
              {lb.del_warn}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="rounded-sm">
              {lb.cancel}
            </Button>
            <Button
              data-testid="confirm-delete-query-btn"
              onClick={deleteQuery}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm"
            >
              {lb.confirm_del}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
